import { useState } from "react";
import { Link } from "react-router";
import { PASSWORD_MIN_LENGTH } from "@wr/contracts";
import { t } from "@wr/i18n";
import { Checkbox, InlineMessage, PasswordField, StrengthMeter, TextField } from "@wr/ui";
import { renderTemplate } from "../lib/rich";
import { LegalLink } from "./LegalAttestation";

/**
 * The email + password + strength + ToS block.
 *
 * **The design spec's §14 point, made literal:** the conversion sheet uses the
 * *same components* as the sign-up tab, because "a conversion is a sign-up with
 * a different frame". This file is that sameness. If the sheet and the tab ever
 * need to diverge, they diverge by props, not by a second copy of a password
 * field with subtly different rules.
 */

export interface CredentialsState {
  email: string;
  password: string;
  tosAccepted: boolean;
  emailTouched: boolean;
  passwordTouched: boolean;
}

export const EMPTY_CREDENTIALS: CredentialsState = {
  email: "",
  password: "",
  tosAccepted: false,
  emailTouched: false,
  passwordTouched: false,
};

export function useCredentials(initial: Partial<CredentialsState> = {}) {
  const [state, setState] = useState<CredentialsState>({ ...EMPTY_CREDENTIALS, ...initial });
  return [state, setState] as const;
}

/** Client-side shape check only — the server is the authority on both. */
export function emailLooksValid(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function passwordLongEnough(password: string): boolean {
  return password.length >= PASSWORD_MIN_LENGTH;
}

export interface CredentialsFieldsProps {
  state: CredentialsState;
  onChange: (next: CredentialsState) => void;
  /** `signup` adds the strength meter and the ToS checkbox (design spec §6). */
  mode: "login" | "signup";
  /** All fields `readonly`, not `disabled` — values stay visible (§6). */
  submitting: boolean;
  /** Rendered on the login tab only, right-aligned under the password. */
  forgotHref?: string;
  /** Shown when a submit was attempted without the ToS box ticked. */
  showTosError?: boolean;
  idPrefix: string;
}

export function CredentialsFields({
  state,
  onChange,
  mode,
  submitting,
  forgotHref,
  showTosError = false,
  idPrefix,
}: CredentialsFieldsProps) {
  // `format-invalid` fires on blur only (§6): telling someone their email is
  // malformed while they are still typing the domain is noise, not help.
  const emailInvalid =
    state.emailTouched && state.email.length > 0 && !emailLooksValid(state.email);
  const passwordInvalid =
    mode === "signup" &&
    state.passwordTouched &&
    state.password.length > 0 &&
    !passwordLongEnough(state.password);

  return (
    <>
      <TextField
        id={`${idPrefix}-email`}
        eyebrow={t("auth.email")}
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        readOnly={submitting}
        value={state.email}
        data-testid="auth-email"
        invalid={emailInvalid}
        helper={emailInvalid ? t("auth.emailInvalid") : undefined}
        onBlur={() => onChange({ ...state, emailTouched: true })}
        onChange={(event) => onChange({ ...state, email: event.target.value })}
      />

      <PasswordField
        id={`${idPrefix}-password`}
        eyebrow={t("auth.password")}
        autoComplete={mode === "signup" ? "new-password" : "current-password"}
        readOnly={submitting}
        value={state.password}
        data-testid="auth-password"
        revealLabel={t("auth.passwordReveal")}
        hideLabel={t("auth.passwordHide")}
        invalid={passwordInvalid}
        helper={
          passwordInvalid ? t("auth.passwordTooShort", { min: PASSWORD_MIN_LENGTH }) : undefined
        }
        onBlur={() => onChange({ ...state, passwordTouched: true })}
        onChange={(event) => onChange({ ...state, password: event.target.value })}
      />

      {forgotHref ? (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Link className="wr-link wr-body-sm" to={forgotHref} data-testid="forgot-link">
            {t("auth.forgot")}
          </Link>
        </div>
      ) : null}

      {mode === "signup" ? (
        <>
          <StrengthMeter
            password={state.password}
            minLength={PASSWORD_MIN_LENGTH}
            ruleLabels={{
              length: t("auth.strength.length", { min: PASSWORD_MIN_LENGTH }),
              case: t("auth.strength.case"),
              number: t("auth.strength.number"),
            }}
          />

          <div>
            <Checkbox
              id={`${idPrefix}-tos`}
              checked={state.tosAccepted}
              disabled={submitting}
              data-testid="auth-tos"
              onChange={(event) => onChange({ ...state, tosAccepted: event.target.checked })}
              label={renderTemplate(t("auth.tos"), {
                tosLink: <LegalLink to="/terms" label={t("auth.tos.terms")} />,
                privacyLink: <LegalLink to="/privacy" label={t("auth.tos.privacy")} />,
              })}
            />
            {showTosError ? (
              <InlineMessage tone="error">{t("auth.error.tosRequired")}</InlineMessage>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}
